import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, TrendingUp, TrendingDown, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const CreditBalance = ({ showPurchase = true }) => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/subscription/balance`);
      setBalance(response.data);
    } catch (error) {
      // User might not have subscription
      console.log('No subscription balance:', error.response?.data?.detail);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/subscription/transactions?limit=20`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const openHistory = () => {
    fetchTransactions();
    setIsHistoryDialogOpen(true);
  };

  if (loading) {
    return (
      <Card className="p-4 border border-slate-200 animate-pulse">
        <div className="h-16 bg-slate-100 rounded"></div>
      </Card>
    );
  }

  if (!balance) {
    return null; // User doesn't have a subscription
  }

  return (
    <>
      <Card className="p-4 border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="credit-balance-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <CreditCard size={24} className="text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-amber-700">Credit Balance</p>
              <p className="text-2xl font-bold text-amber-800">{balance.credits}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={openHistory}
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="view-history-btn"
            >
              <History size={14} />
            </Button>
            {showPurchase && (
              <Button
                size="sm"
                onClick={() => navigate('/credits')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="purchase-credits-btn"
              >
                Buy Credits
              </Button>
            )}
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="text-xs text-amber-600">
            {balance.subscription_name} &bull; {balance.status === 'active' ? 'Active' : balance.status}
          </p>
        </div>
      </Card>

      {/* Transaction History Dialog */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Credit History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {transactions.length > 0 ? (
              transactions.map((trans) => (
                <div key={trans.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    {trans.amount > 0 ? (
                      <TrendingUp size={16} className="text-emerald-600" />
                    ) : (
                      <TrendingDown size={16} className="text-rose-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-900">{trans.description}</p>
                      <p className="text-xs text-slate-500">{new Date(trans.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <span className={`font-bold ${trans.amount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {trans.amount > 0 ? '+' : ''}{trans.amount}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-4">No transactions yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreditBalance;
